import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  ADatePicker,
  AIcon,
  AListItem,
  AMount,
  APopover,
  ATextInput,
  useADateRange
} from "@cisco-sbg-ui/magna-react";
import classNames from "classnames";
import {
  getLastCustomRangeFilter,
  setLastCustomRangeFilter
} from "config/localStorage";
import {kebabCase} from "lodash-es";
import moment from "moment";
import {useSelector} from "react-redux";
import {DropdownMenu} from "components/DropdownMenu";
import {IncidentsContext} from "contexts/IncidentsContext";
import {selectIncidentsLoading} from "store/selectors";
import {sendIncidentsDateRangeTelemetry} from "api/telemetry/incidentsTelemetrics";
import {DATE_RANGES, DATE_RANGES_MAP} from "models/options";
import {startBeforeOREqualToEnd} from "utils/dateutils";
import css from "./IncidentsDateRange.module.scss";

const IncidentsDateRange = () => {
  const dropdownRef = useRef();
  const customRangeRef = useRef();
  const isLoading = useSelector(selectIncidentsLoading);

  const {filterSettings, dispatchFilterSettings} = useContext(IncidentsContext);
  const {dateRangeFilter: selectedRange} = filterSettings;

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selected, setSelected] = useState(
    DATE_RANGES_MAP[selectedRange.filter]
  );
  const {value: calendarValue, onChange: onCalendarValueChange} =
    useADateRange();

  useEffect(() => {
    if (
      selectedRange &&
      selectedRange.filter &&
      selectedRange.filter !== DATE_RANGES_MAP.CUSTOM.value
    ) {
      setSelected(DATE_RANGES_MAP[selectedRange.filter]);
    }
  }, [selectedRange]);

  useEffect(() => {
    if (
      selectedRange &&
      selectedRange.filter &&
      selectedRange.filter === DATE_RANGES_MAP.CUSTOM.value
    ) {
      const startDate = selectedRange.startDate
        ? moment(selectedRange.startDate).toDate()
        : null;
      const endDate = selectedRange.endDate
        ? moment(selectedRange.endDate).toDate()
        : null;
      onCalendarValueChange(startDate);
      onCalendarValueChange(endDate);
    } else {
      onCalendarValueChange(null);
    }
  }, [selectedRange, onCalendarValueChange]);

  const handlePrefinedRange = (dateRange) => {
    setSelected(dateRange);
    dispatchFilterSettings({dateRangeFilter: {filter: dateRange.value}});
    dropdownRef.current.closeMenu();
  };

  const handleCustomDateRange = (start, end) => {
    const lastDateRange = getLastCustomRangeFilter();
    const defaultEndDate = getDefaultEndDate(end, lastDateRange);
    const defaultStartDate = getDefaultStartDate(start, lastDateRange);

    setSelected(DATE_RANGES_MAP.CUSTOM);

    dispatchFilterSettings({
      dateRangeFilter: {
        filter: DATE_RANGES_MAP.CUSTOM.value,
        startDate: defaultStartDate,
        endDate: defaultEndDate
      }
    });

    setLastCustomRangeFilter({
      startDate: defaultStartDate,
      endDate: defaultEndDate
    });
  };

  const handleCustomDateRangeChange = (changedDate) => {
    const [currentStart, currentEnd] = calendarValue;

    const startDateChanged = !currentStart || (!!currentStart && !!currentEnd);
    const endDateChanged = !!currentStart && !currentEnd;

    if (startDateChanged) {
      onCalendarValueChange(changedDate);
    } else if (endDateChanged) {
      const startDate = currentStart ? moment(currentStart) : null;
      const endDate = changedDate ? moment(changedDate) : null;

      if (startBeforeOREqualToEnd(startDate, endDate, "day")) {
        onCalendarValueChange(changedDate);
        handleCustomDateRange(startDate.toDate(), endDate.toDate());
        setCalendarOpen(false);
        dropdownRef.current.closeMenu();
      }
    }
  };

  const getDefaultStartDate = (inputDate, lastDateRange) =>
    inputDate
      ? moment(inputDate).toISOString()
      : lastDateRange && lastDateRange.startDate
      ? lastDateRange.startDate
      : moment().subtract(90, "d");

  const getDefaultEndDate = (inputDate, lastDateRange) =>
    inputDate
      ? moment(inputDate).toISOString()
      : lastDateRange && lastDateRange.endDate
      ? lastDateRange.endDate
      : moment();

  const isPredefinedRange = useCallback(
    (dateRange) => dateRange.value !== DATE_RANGES_MAP.CUSTOM.value,
    [DATE_RANGES_MAP]
  );

  const formatDate = (date) => (date ? moment(date).format("MM/DD/YY") : "");

  const setDisplayValue = useMemo(
    () =>
      selected.value === DATE_RANGES_MAP.CUSTOM.value
        ? `${formatDate(selectedRange.startDate)}-${formatDate(
            selectedRange.endDate
          )}`
        : selected.name,
    [selected, selectedRange]
  );

  return (
    <div
      className={classNames("incident-date-range", css.dateRangeInput)}
      data-testid={"IncidentsDateRange-Container"}>
      <DropdownMenu
        data-testid={"IncidentsDateRange-DropdownMenu"}
        ref={dropdownRef}
        buttonVariant="tertiary"
        buttonProps={{
          disabled: isLoading,
          medium: true
        }}
        buttonContent={
          <>
            <AIcon left size={12}>
              clock
            </AIcon>
            <span>{setDisplayValue}</span>
          </>
        }
        buttonSpanProps={{className: css.btnSpan}}
        menuClass={css.menu}
        closeMenuOnClick={false}>
        {DATE_RANGES.filter(isPredefinedRange).map((dateRange) => (
          <AListItem
            key={`option-${kebabCase(dateRange.value)}`}
            id={`date-range-option--${kebabCase(dateRange.value)}`}
            className="date-range-option justify-left"
            onClick={() => {
              handlePrefinedRange(dateRange);
              sendIncidentsDateRangeTelemetry(dateRange.name);
            }}
            selected={dateRange.value === selected.value}>
            {dateRange.name}
          </AListItem>
        ))}
        <AListItem
          id={`date-range-option--${kebabCase(DATE_RANGES_MAP.CUSTOM.value)}`}
          className="date-range-option justify-left"
          onClick={() => {
            handleCustomDateRange();
            sendIncidentsDateRangeTelemetry(DATE_RANGES_MAP.CUSTOM.name);
          }}
          selected={DATE_RANGES_MAP.CUSTOM.value === selected.value}>
          {DATE_RANGES_MAP.CUSTOM.name}
        </AListItem>
        {selectedRange.filter === DATE_RANGES_MAP.CUSTOM.value && (
          <>
            <div
              ref={customRangeRef}
              className={classNames(css.dateInputGrp, "d-flex")}
              onClick={() => setCalendarOpen(true)}>
              <ATextInput
                medium
                className={classNames(css.dateInput, "ma-1")}
                value={formatDate(calendarValue[0])}
                readOnly></ATextInput>
              <ATextInput
                medium
                className={classNames(css.dateInput, "ma-1")}
                value={formatDate(calendarValue[1])}
                readOnly></ATextInput>
            </div>
            <AMount
              style={{position: "relative"}}
              data-testid={"IncidentsDateRange-Popover-wrapper"}>
              <APopover
                anchorRef={customRangeRef}
                open={calendarOpen}
                onClose={() => setCalendarOpen(false)}
                placement="bottom-right"
                focusOnOpen={false}
                className={css.dateRangePopover}>
                <ADatePicker
                  className="date-range-menu__date-picker"
                  value={calendarValue}
                  onChange={handleCustomDateRangeChange}
                  maxDate={moment().endOf("day").toDate()}
                  minDate={moment().subtract(1, "year").toDate()}
                />
              </APopover>
            </AMount>
          </>
        )}
      </DropdownMenu>
    </div>
  );
};

export default IncidentsDateRange;
